import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SESSION_TEXT_ITEMS, SAVED_SEARCH_TEXT_ITEMS } from '../constants';
import Button from '@/components/atoms/Button/Button';
import Icon from '@/components/atoms/Icon';
import { CustomEvents } from '@/libs/eventBus/constants';
import EventBus from '@/libs/eventBus/eventBus';
import useSavedSearch from '@/hooks/useSavedSearch';

interface PopupPayload {
  top: number;
  button: HTMLElement;
}

export default function TextNavPopover() {
  const [state, setState] = useState<PopupPayload | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { eventId, broadcastId, id } = useParams<{ eventId?: string; broadcastId?: string; id?: string }>();
  const { isSavedSearchPage } = useSavedSearch();
  const items = isSavedSearchPage ? SAVED_SEARCH_TEXT_ITEMS : SESSION_TEXT_ITEMS;

  const close = useCallback(() => setState(null), []);

  useEffect(() => {
    const handler = (payload: PopupPayload) => {
      setState(prev => (prev !== null ? null : payload));
    };
    const eventListener = EventBus.on(CustomEvents.OpenTextNavPopup, handler);
    return () => EventBus.off(CustomEvents.OpenTextNavPopup, eventListener);
  }, []);

  useEffect(() => {
    if (!state) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (state.button.contains(e.target as Node)) {
        return;
      }
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [state, close]);

  if (!state) {
    return <></>;
  }

  const handleOptionClick = (path: string) => {
    const url = isSavedSearchPage ? `/search/${id}/${path}` : `/${eventId}/${broadcastId}/${path}`;
    navigate(url);
    close();
  };

  return (
    <div
      ref={popoverRef}
      data-testid="text-nav-popover"
      className="absolute left-20 z-[100] min-w-[160px] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg"
      style={{ top: `${state.top}px` }}
    >
      {items.map(item => (
        <Button
          key={item.path}
          trackingId={`text-nav-option-${item.path}`}
          variation="none"
          className="w-full !justify-start gap-3 rounded-none px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-100"
          onClick={() => handleOptionClick(item.path)}
        >
          <Icon name={item.iconName} className="size-5" />
          {item.label}
        </Button>
      ))}
    </div>
  );
}
